import { AuthService } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

export interface NaturalLanguageQueryRequest {
    query: string;
    companyNo: string;
    model?: string;
    maxRows?: number;
}

export interface ColumnDefinition {
    name: string;
    displayName: string;
    dataType: string;
    format?: string;
    width?: number;
    sortable: boolean;
    filterable: boolean;
}

export interface DefaultSort {
    columnName: string;
    direction: 'asc' | 'desc';
}

export interface HighlightRule {
    columnName: string;
    condition: string;
    value: any;
    color: string;
}

export interface PresentationHints {
    title?: string;
    description?: string;
    defaultSort?: DefaultSort;
    highlightRules?: HighlightRule[];
}

export interface NaturalLanguageQueryResponse {
    success: boolean;
    queryId: string;
    generatedSQL?: string;
    explanation?: string;
    executionTimeMs: number;
    rowCount: number;
    tokensUsed?: number;
    timestamp: string;
    columns: ColumnDefinition[];
    rows: Record<string, any>[];
    presentation?: PresentationHints;
    errorMessage?: string;
    errorType?: string;
}

// Data Processing Types
export interface SelectedDataset {
    queryId: string;
    originalQuery: string;
    rows: Record<string, any>[];
}

export interface DataProcessingRequest {
    query: string;
    companyNo: string;
    selectedDatasets: SelectedDataset[];
    model?: string;
}

export interface TextOutput {
    content: string;
    format: string;
}

export interface ChartSeries {
    name: string;
    data: Record<string, any>[];
    color?: string;
}

export interface ChartAxisConfig {
    label?: string;
    dataKey: string;
    format?: string;
}

export interface ChartOutput {
    chartType: string;
    title: string;
    series: ChartSeries[];
    xAxis?: ChartAxisConfig;
    yAxis?: ChartAxisConfig;
}

export interface TableOutput {
    title?: string;
    description?: string;
    columns: ColumnDefinition[];
    rows: Record<string, any>[];
}

export interface BinaryOutput {
    mimeType: string;
    base64Data: string;
    filename: string;
}

export interface DataProcessingResponse {
    success: boolean;
    outputType: 'text' | 'chart' | 'pdf' | 'image' | 'table';
    data: TextOutput | ChartOutput | TableOutput | BinaryOutput | any;
    explanation?: string;
    executionTimeMs: number;
    tokensUsed?: number;
    timestamp: string;
    errorMessage?: string;
    errorType?: string;
}

class ClaudeQueryService {
    async executeQuery(request: NaturalLanguageQueryRequest): Promise<NaturalLanguageQueryResponse> {
        const response = await AuthService.authenticatedPost(
            `${API_BASE_URL}/claude-query/execute`,
            request
        );

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || `Failed to execute query: ${response.status}`);
        }

        return await response.json();
    }

    async processData(request: DataProcessingRequest): Promise<DataProcessingResponse> {
        const response = await AuthService.authenticatedPost(
            `${API_BASE_URL}/claude-query/process-data`,
            request
        );

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || `Failed to process data: ${response.status}`);
        }

        return await response.json();
    }

    async getSchema(companyNo: string): Promise<string> {
        const response = await AuthService.authenticatedGet(
            `${API_BASE_URL}/claude-query/schema/${companyNo}`
        );

        if (!response.ok) {
            throw new Error(`Failed to get schema: ${response.status}`);
        }

        return await response.text();
    }

    async getCurrentSchema(): Promise<string> {
        const response = await AuthService.authenticatedGet(
            `${API_BASE_URL}/claude-query/schema`
        );

        if (!response.ok) {
            throw new Error(`Failed to get schema: ${response.status}`);
        }

        return await response.text();
    }
}

export const claudeQueryService = new ClaudeQueryService();
